import {
	ContestPerformanceCardWrapper,
	ContestPerformanceCardTitle,
	ContestPerformanceCardDate,
	ContestPerformanceCardHeader,
	ContestPerformanceCardName,
	ContestPerformanceCardContent,
	ContestPerformanceCardContentWrapper,
	ContestPerformanceCardContentValue,
	ScheduledLabelWrapper,
} from './contestPerformanceCard.style';

import { LinkButton } from '../../../button/LinkButton';
import { ProgressBar } from '../progressBar/ProgressBar';
import { PodiumAvatar } from '../../../podiumAvatar/PodiumAvatar';
import { Label } from '../../../label/Label';
import type { LabelVariant } from '../../../label/LabelVariant';
  
export type ContestStatus = 'scheduled' | 'active' | 'completed';
  
export type ContestData = {
	name: string;
	avatarUrl: string;
	placement: number;
	value: number;
	target: number;
	targettype?: string;
  
	status?: ContestStatus;
	startsText?: string;
	hideProgress?: boolean;
  };
  
export type ContestPerformanceCardProps = {
	title: string;
	link: string;
	linkText: string;
	start_date: string;
	end_date: string;
	contestData: ContestData;
  };
  
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// The API sends full timestamps ('2026-08-24T00:00:00Z'); older callers pass a bare day. A bare
// day reads as local midnight, so both display in the viewer's zone.
const parseDate = (isoDate: string): Date | null => {
	if (!isoDate) return null;
	const date = new Date(DATE_ONLY.test(isoDate) ? `${isoDate}T00:00` : isoDate);
	return Number.isNaN(date.getTime()) ? null : date;
};

const formatShortDate = (isoDate: string): string => {
	const date = parseDate(isoDate);
	if (!date) return isoDate;
	return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

export const getContestPhase = (
	status: ContestStatus | undefined,
	start: string,
	end: string,
	now = new Date(),
): ContestStatus => {
	if (status) return status;

	const startDate = parseDate(start);
	const endDate = parseDate(end);
	if (!startDate || !endDate) return 'active';

	// A bare end day still runs until that day is over.
	if (DATE_ONLY.test(end)) endDate.setDate(endDate.getDate() + 1);

	if (now < startDate) return 'scheduled';
	if (now >= endDate) return 'completed';
	return 'active';
};

const getPhaseLabel = (phase: ContestStatus, start: string, end: string, startsText?: string) => {
	switch (phase) {
	case 'scheduled':
		return `${startsText || 'Starts'} ${formatShortDate(start)}`;
	case 'completed':
		return `Ended ${formatShortDate(end)}`;
	case 'active':
	default:
		return 'In Progress';
	}
};
  
const getPhaseVariant = (phase: ContestStatus): LabelVariant => {
	if (phase === 'scheduled') return 'informational';
	if (phase === 'completed') return 'neutral';
	return 'warning'; // active
};

const formatDateRange = (start: string, end: string): string => {
	const s = formatShortDate(start);
	const e = formatShortDate(end);
  
	if (s && e) return `${s} – ${e}`;
	if (s) return s;
	if (e) return e;
	return '';
};
  
export const ContestPerformanceCard = ({
	title,
	link,
	linkText,
	start_date,
	end_date,
	contestData,
}: ContestPerformanceCardProps) => {
	const phase = getContestPhase(contestData.status, start_date, end_date);
  
	const phaseLabel = getPhaseLabel(phase, start_date, end_date, contestData.startsText);
	const phaseVariant = getPhaseVariant(phase);
  
	const isScheduled = phase === 'scheduled';
  
	return (
		<ContestPerformanceCardWrapper
		>
			<ScheduledLabelWrapper>
				<Label variant={phaseVariant} size="large" rounded>
					{phaseLabel}
				</Label>
			</ScheduledLabelWrapper>
  
			<ContestPerformanceCardHeader>
				<ContestPerformanceCardTitle>{title}</ContestPerformanceCardTitle>
			</ContestPerformanceCardHeader>
  
			<ContestPerformanceCardDate>
				{formatDateRange(start_date, end_date)}
				{!isScheduled ? (
					<LinkButton
						href={link}
						variant="link"
						size="min"
						iconPosition="right"
						label={linkText}
					/>
				) : null}
			</ContestPerformanceCardDate>
  
			{!isScheduled ? (
				<ContestPerformanceCardContentWrapper>
					<PodiumAvatar
						size={56}
						name={contestData.name}
						avatarUrl={contestData.avatarUrl}
						placement={contestData.placement}
						showSparkles={contestData.placement === 1}
					/>

					<ContestPerformanceCardContent>
						<ContestPerformanceCardName>{contestData.name}</ContestPerformanceCardName>
  
						{!contestData.hideProgress ? (
							<ProgressBar
								value={contestData.value}
								target={contestData.target}
								targettype={contestData.targettype}
								inline={false}
								useDynamicColors
								showPercent
								showValueLabel
							/>
						) : (
							<ContestPerformanceCardContentValue>
								{contestData.value}
								{contestData.targettype ? ` / ${contestData.targettype}` : ''}
							</ContestPerformanceCardContentValue>
						)}
					</ContestPerformanceCardContent>
				</ContestPerformanceCardContentWrapper>
			) : null}
		</ContestPerformanceCardWrapper>
	);
};
